import { useState } from "react"
import { track } from "@/lib/analytics"
import type { SocialPost } from "@/types"
import { csrfToken } from "@/lib/csrf"
import { toast } from "sonner"
import { Badge } from "@/components/ui/badge"
import SocialPostDetailDialog from "@/components/dashboard/social-post-detail-dialog"
import PlatformIcon from "@/components/dashboard/platform-icon"
import { PLATFORM_LABELS, STATUS_STYLES, POST_TYPE_LABELS } from "@/lib/social-post-constants"
import SocialPostMetrics from "./social-post-metrics"

export default function SocialPostsList({
  posts,
  videoId,
  renderId,
  onPostRemoved,
}: {
  posts: SocialPost[]
  videoId: string
  renderId: string
  onPostRemoved?: () => void
}) {
  const [selectedPost, setSelectedPost] = useState<SocialPost | null>(null)

  if (posts.length === 0) return null

  async function handleCancel(post: SocialPost) {
    try {
      const response = await fetch(
        `/dashboard/videos/${videoId}/renders/${renderId}/social_posts/${post.id}`,
        {
          method: "DELETE",
          headers: { "X-CSRF-Token": csrfToken() },
        },
      )

      if (response.ok) {
        track("social_post_cancelled", { post_id: post.id })
        toast.success("Post deleted")
        onPostRemoved?.()
      } else {
        toast.error("Could not delete post")
      }
    } catch {
      toast.error("Something went wrong")
    }
  }

  async function handleRetry(post: SocialPost) {
    try {
      const response = await fetch(
        `/dashboard/videos/${videoId}/renders/${renderId}/social_posts/${post.id}/retry`,
        {
          method: "POST",
          headers: { "X-CSRF-Token": csrfToken() },
        },
      )

      if (response.ok) {
        track("social_post_retried", { platform: post.platform })
        toast.success("Retrying publish...")
        onPostRemoved?.()
      } else {
        toast.error("Could not retry")
      }
    } catch {
      toast.error("Something went wrong")
    }
  }

  return (
    <div>
      <h3 className="mb-3 text-[11px] font-semibold uppercase tracking-widest text-warm-grey">
        Social posts
      </h3>
      <div className="space-y-2">
        {posts.map((post) => {
          const statusStyle = STATUS_STYLES[post.status] || STATUS_STYLES.pending

          return (
            <div
              key={post.id}
              className="cursor-pointer rounded-xl border border-stone/30 bg-white px-4 py-3 transition-colors hover:bg-cream/30"
              onClick={() => setSelectedPost(post)}
            >
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2.5">
                  <PlatformIcon platform={post.platform} className="size-4 shrink-0" />
                  <span className="text-sm font-medium text-charcoal">
                    {PLATFORM_LABELS[post.platform] || post.platform}
                  </span>
                  <Badge variant="outline" className="text-[10px] border-stone/20 text-warm-grey">
                    {POST_TYPE_LABELS[post.post_type] || post.post_type}
                  </Badge>
                  <Badge
                    variant="outline"
                    className={`text-[10px] ${statusStyle.className}`}
                  >
                    {post.status === "uploading" && (
                      <svg className="mr-1 size-3 animate-spin" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                    )}
                    {statusStyle.label}
                  </Badge>
                </div>

                <div className="flex items-center gap-2">
                  {post.status === "published" && post.platform_post_url && (
                    <a
                      href={post.platform_post_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs font-medium text-red hover:underline"
                      onClick={(e) => e.stopPropagation()}
                    >
                      View on {PLATFORM_LABELS[post.platform]}
                    </a>
                  )}
                </div>
              </div>

              {/* Caption preview */}
              {post.caption && (
                <p className="mt-1.5 line-clamp-2 text-xs text-warm-grey">
                  {post.caption}
                </p>
              )}

              {/* Date info */}
              <div className="mt-1.5 flex items-center gap-3">
                {post.status === "scheduled" && post.scheduled_at && (
                  <span className="text-[11px] text-warm-grey/60">
                    Scheduled for{" "}
                    {new Date(post.scheduled_at).toLocaleString("en-GB", {
                      day: "numeric",
                      month: "short",
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </span>
                )}
                {post.status === "published" && post.published_at && (
                  <span className="text-[11px] text-warm-grey/60">
                    Published{" "}
                    {new Date(post.published_at).toLocaleDateString("en-GB", {
                      day: "numeric",
                      month: "short",
                      year: "numeric",
                    })}
                  </span>
                )}
              </div>

              {/* Error message */}
              {post.status === "failed" && post.error_message && (
                <p className="mt-1.5 text-xs text-red/80">{post.error_message}</p>
              )}

              {/* Metrics */}
              {post.status === "published" && (
                <SocialPostMetrics metrics={post.metrics} />
              )}
            </div>
          )
        })}
      </div>

      <SocialPostDetailDialog
        post={selectedPost}
        open={!!selectedPost}
        onOpenChange={(open) => !open && setSelectedPost(null)}
        onDelete={handleCancel}
        onRetry={handleRetry}
        showVideoLink={false}
      />
    </div>
  )
}
